import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Avatar, Stack, IconButton, Menu, MenuItem,
  Divider, CircularProgress, Popover, Button, Chip, Link, Rating, Tooltip,
} from '@mui/material';
import {
  MoreVert, ScubaDiving, Delete, Thermostat, AccessTime,
  ChatBubbleOutline, Send, ExpandMore, Close, PushPin, Flag, Block, Star, Place, Public as PublicIcon,
} from '@mui/icons-material';
import { Post, PostType, Comment, postService, ReactionType, ReactionTypeValue, Reaction } from '../services/postService';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { diveShareService } from '../services/diveShareService';
import { useNotification } from './NotificationContext';
import DiveSparkline from './DiveSparkline';
import PostDiveMap from './PostDiveMap';
import BlockUserDialog from './BlockUserDialog';
import ReportDialog from './ReportDialog';
import PostMedia from './PostMedia';
import NereusCaution from './NereusCaution';
import SafetyNote from './SafetyNote';
import MentionTextField, { NEREUS_MENTION_ID } from './MentionTextField';
import MentionText from './MentionText';
import { requestTagInvites } from '../utils/invite';
import NereusIcon from './NereusIcon';
import EquipmentTypeIcon from './EquipmentTypeIcon';
import { EquipmentType } from '../types/Equipment';
import { formatDepth, formatTemperature } from '../utils/unitConversions';
import { OptionsManager } from '../utils/optionsManager';
import { formatVisibility, formatWaterTemp } from '../utils/conditions';

function initials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
}

function formatDuration(duration: string): string {
  const match = duration.match(/(\d{2}):(\d{2}):(\d{2})/);
  if (!match) return duration;
  const [, hours, minutes] = match;
  const h = parseInt(hours, 10);
  const m = parseInt(minutes, 10);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

const REACTIONS: { type: ReactionTypeValue; emoji: string; label: string }[] = [
  { type: ReactionType.Ok, emoji: '👌', label: 'OK' },
  { type: ReactionType.Shaka, emoji: '🤙', label: 'Cool' },
  { type: ReactionType.Heart, emoji: '❤️', label: 'Love' },
];

const reactionEmoji = (type: ReactionTypeValue | null | undefined): string =>
  REACTIONS.find(r => r.type === type)?.emoji ?? REACTIONS[0].emoji;

interface PostCardProps {
  post: Post;
  currentUserId: string | null;
  onDelete: (postId: string) => void;
  /** True when the viewer is an Admin/Owner of this post's Group/Club — enables the pin/unpin menu item. */
  canManageGroupOrClub?: boolean;
  onPinChanged?: (post: Post) => void;
  /** Called after the author features/unfeatures their own post, with the updated post. */
  onFeatureChanged?: (post: Post) => void;
}

// A repost is a thin wrapper with no content of its own — this renders just the "Reposted by
// X" byline and delegates everything else (reactions, comments, dive log body) to a nested
// PostCard for the ORIGINAL post, fetched once and cached locally. Reposting is blocked
// server-side against a repost's own postId, so this never recurses more than one level deep.
const RepostWrapper: React.FC<PostCardProps & { post: Post & { repostedFromPostId: string } }> = ({ post, currentUserId, onDelete }) => {
  const navigate = useNavigate();
  const [original, setOriginal] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    postService.getPost(post.repostedFromPostId)
      .then(p => { if (!cancelled) { if (p) setOriginal(p); else setNotFound(true); } })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [post.repostedFromPostId]);

  return (
    <Paper elevation={2} sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, color: 'text.secondary' }}>
        <ScubaDiving sx={{ fontSize: 16, transform: 'scaleX(-1)' }} />
        <Typography variant="caption">
          Reposted by{' '}
          <Typography
            component="span"
            variant="caption"
            sx={{ fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate(`/u/${post.authorUserId}`)}
          >
            {post.authorDisplayName}
          </Typography>
          {' · '}{new Date(post.createdAt).toLocaleDateString()}
        </Typography>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!loading && (notFound || !original) && (
        <Typography variant="body2" color="text.secondary">This post is no longer available.</Typography>
      )}

      {!loading && original && (
        <PostCardBody post={original} currentUserId={currentUserId} onDelete={onDelete} nested />
      )}
    </Paper>
  );
};

const PostCard: React.FC<PostCardProps> = (props) => {
  if (props.post.repostedFromPostId) {
    return <RepostWrapper {...props} post={props.post as Post & { repostedFromPostId: string }} />;
  }
  return (
    <Paper elevation={2} sx={{ p: 2.5, mb: 2 }}>
      <PostCardBody {...props} />
    </Paper>
  );
};

const PostCardBody: React.FC<PostCardProps & { nested?: boolean }> = ({ post, currentUserId, onDelete, canManageGroupOrClub, onPinChanged, onFeatureChanged, nested }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [blockOpen, setBlockOpen] = useState(false);
  const [authorBlocked, setAuthorBlocked] = useState(false);
  const units = OptionsManager.getInstance().getOption('units');
  const isOwnPost = currentUserId === post.authorUserId;
  const [coLogging, setCoLogging] = useState(false);
  const [reposting, setReposting] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ type: 'Post' } | { type: 'Comment'; commentId: string } | null>(null);

  const canPin = !!canManageGroupOrClub && (post.visibility === 'Group' || post.visibility === 'Club');

  const handleToggleFeature = async () => {
    setMenuAnchor(null);
    try {
      const updated = post.isFeatured ? await postService.unfeaturePost(post.postId) : await postService.featurePost(post.postId);
      onFeatureChanged?.(updated);
      showSuccess(updated.isFeatured ? 'Featured on your profile' : 'Removed from featured');
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to update featured posts');
    }
  };

  const handleTogglePin = async () => {
    if (pinning) return;
    setMenuAnchor(null);
    setPinning(true);
    try {
      const updated = post.isPinned ? await postService.unpinPost(post.postId) : await postService.pinPost(post.postId);
      onPinChanged?.(updated);
      showSuccess(updated.isPinned ? 'Post pinned' : 'Post unpinned');
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to update pin');
    } finally {
      setPinning(false);
    }
  };

  const handleRepost = async () => {
    if (reposting) return;
    setReposting(true);
    try {
      await postService.repostPost(post.postId);
      showSuccess('Reposted to your feed');
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to repost');
    } finally {
      setReposting(false);
    }
  };

  const canCoLog = !!currentUserId && !isOwnPost && post.type === PostType.DiveLog
    && !!post.diveSnapshot && post.taggedUserIds.includes(currentUserId);

  const handleCoLog = async () => {
    if (coLogging) return;
    setCoLogging(true);
    try {
      const dive = await diveShareService.coLogFromPost(post.postId);
      showSuccess('Dive added to your log — fill in your own gas, weight, and gear');
      navigate(`/dive/${dive.id}`);
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to add this dive to your log');
    } finally {
      setCoLogging(false);
    }
  };

  const [viewerReactions, setViewerReactions] = useState<ReactionTypeValue[]>(
    post.viewerHasReacted ? post.viewerReactionTypes : []
  );
  const [reactionCount, setReactionCount] = useState(post.reactionCount);
  const [reactorCount, setReactorCount] = useState(post.reactorCount);
  const [reactionBusy, setReactionBusy] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState<null | HTMLElement>(null);

  const [reactorListAnchor, setReactorListAnchor] = useState<null | HTMLElement>(null);
  const [reactors, setReactors] = useState<Reaction[]>([]);
  const [reactorProfiles, setReactorProfiles] = useState<Record<string, UserPublicProfile>>({});
  const [reactorsLoading, setReactorsLoading] = useState(false);

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentCount, setCommentCount] = useState(post.commentCount);
  const [newComment, setNewComment] = useState('');
  const [commentTaggedUserIds, setCommentTaggedUserIds] = useState<string[]>([]);
  const [commentTaggedGroupIds, setCommentTaggedGroupIds] = useState<string[]>([]);
  const [commentTaggedClubIds, setCommentTaggedClubIds] = useState<string[]>([]);
  const [postingComment, setPostingComment] = useState(false);

  const loadComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      setComments(await postService.getComments(post.postId));
    } finally {
      setCommentsLoading(false);
    }
  }, [post.postId]);

  useEffect(() => {
    if (commentsOpen) loadComments();
  }, [commentsOpen, loadComments]);

  // Reactor names are only fetched when the list is actually opened, not eagerly for
  // every post in the feed — resolving a name per reactor is one profile call each, same
  // batch-and-cache pattern DiveBuddies uses for buddy display names.
  const openReactorList = async (e: React.MouseEvent<HTMLElement>) => {
    if (reactionCount === 0) return;
    setReactorListAnchor(e.currentTarget);
    setReactorsLoading(true);
    try {
      const fetched = await postService.getReactions(post.postId);
      setReactors(fetched);
      const missing = Array.from(new Set(fetched.map(r => r.userId))).filter(id => !reactorProfiles[id]);
      if (missing.length > 0) {
        const results = await Promise.allSettled(missing.map(id => buddyService.getUserProfile(id)));
        const next: Record<string, UserPublicProfile> = {};
        results.forEach((r, i) => {
          if (r.status === 'fulfilled') next[missing[i]] = r.value;
        });
        if (Object.keys(next).length > 0) {
          setReactorProfiles(prev => ({ ...prev, ...next }));
        }
      }
    } finally {
      setReactorsLoading(false);
    }
  };
  const closeReactorList = () => setReactorListAnchor(null);

  // Before the list has ever been opened this is just a count ("13 reactions"); once we
  // know who reacted (from a prior open, cached in reactorProfiles) it upgrades to
  // "Sarah and 12 others reacted" — the social-proof framing the count alone doesn't give.
  // Compares against reactorCount (unique people), not reactionCount (total instances),
  // since stacking lets one person contribute more than one reaction type.
  const reactorLabel = (): string => {
    if (reactorCount === 0) return '';
    const firstReactor = reactors[0];
    const firstName = firstReactor ? reactorProfiles[firstReactor.userId]?.displayName : undefined;
    if (firstName && reactors.length === reactorCount) {
      const others = reactorCount - 1;
      return others === 0 ? `${firstName} reacted` : `${firstName} and ${others} other${others === 1 ? '' : 's'} reacted`;
    }
    return reactorCount === 1 ? '1 reaction' : `${reactorCount} reactions`;
  };

  const openPicker = (e: React.MouseEvent<HTMLElement>) => {
    if (!currentUserId || reactionBusy) return;
    setPickerAnchor(e.currentTarget);
  };

  const closePicker = () => setPickerAnchor(null);

  // Toggles one reaction type on/off, stacking alongside whatever other types the viewer
  // already left — the picker stays open so more than one can be picked in one visit.
  const chooseReaction = async (type: ReactionTypeValue) => {
    if (!currentUserId || reactionBusy) return;
    const previous = viewerReactions;
    const wasReactor = previous.length > 0;
    const alreadyHasType = previous.includes(type);
    const next = alreadyHasType ? previous.filter(t => t !== type) : [...previous, type];

    setReactionBusy(true);
    setViewerReactions(next);
    setReactionCount(c => c + (alreadyHasType ? -1 : 1));
    if (!wasReactor && next.length > 0) setReactorCount(c => c + 1);
    else if (wasReactor && next.length === 0) setReactorCount(c => Math.max(0, c - 1));
    try {
      await postService.addReaction(post.postId, type);
    } catch {
      setViewerReactions(previous);
      setReactionCount(post.reactionCount);
      setReactorCount(post.reactorCount);
    } finally {
      setReactionBusy(false);
    }
  };

  const removeAllReactions = async () => {
    closePicker();
    if (!currentUserId || reactionBusy || viewerReactions.length === 0) return;
    setReactionBusy(true);
    setViewerReactions([]);
    setReactionCount(c => Math.max(0, c - viewerReactions.length));
    setReactorCount(c => Math.max(0, c - 1));
    try {
      await postService.removeReaction(post.postId);
    } catch {
      setViewerReactions(post.viewerHasReacted ? post.viewerReactionTypes : []);
      setReactionCount(post.reactionCount);
      setReactorCount(post.reactorCount);
    } finally {
      setReactionBusy(false);
    }
  };

  // Click on the reaction button itself: clear every reaction if already reacted, otherwise open the picker.
  const handleReactionButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    if (viewerReactions.length > 0) {
      removeAllReactions();
    } else {
      openPicker(e);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || postingComment) return;
    setPostingComment(true);
    try {
      const comment = await postService.addComment(post.postId, newComment.trim(), commentTaggedUserIds, commentTaggedGroupIds, commentTaggedClubIds);
      setComments(prev => [...prev, comment]);
      setCommentCount(c => c + 1);
      setNewComment('');
      setCommentTaggedUserIds([]);
      setCommentTaggedGroupIds([]);
      setCommentTaggedClubIds([]);
    } finally {
      setPostingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    await postService.deleteComment(post.postId, commentId);
    // Deleting a top-level comment orphans its replies rather than cascading — same choice
    // the backend makes, and the same choice DiveTrip deletion makes for its child dives.
    setComments(prev => prev.filter(c => c.commentId !== commentId));
    setCommentCount(c => Math.max(0, c - 1));
  };

  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyTaggedUserIds, setReplyTaggedUserIds] = useState<string[]>([]);
  const [replyTaggedGroupIds, setReplyTaggedGroupIds] = useState<string[]>([]);
  const [replyTaggedClubIds, setReplyTaggedClubIds] = useState<string[]>([]);
  const [postingReply, setPostingReply] = useState(false);

  const startReply = (commentId: string) => {
    setReplyingToId(commentId);
    setReplyText('');
    setReplyTaggedUserIds([]);
    setReplyTaggedGroupIds([]);
    setReplyTaggedClubIds([]);
  };

  const handleAddReply = async () => {
    if (!replyingToId || !replyText.trim() || postingReply) return;
    setPostingReply(true);
    try {
      const reply = await postService.addComment(
        post.postId, replyText.trim(), replyTaggedUserIds, replyTaggedGroupIds, replyTaggedClubIds, replyingToId
      );
      setComments(prev => [...prev, reply]);
      setCommentCount(c => c + 1);
      setReplyingToId(null);
    } finally {
      setPostingReply(false);
    }
  };

  // Nesting is single-level: replies are grouped by their parent's commentId, and a reply
  // itself never has replies. Comments with a parentCommentId pointing at nothing (the
  // parent was deleted) simply never surface — they're not top-level and no longer findable.
  const topLevelComments = comments.filter(c => !c.parentCommentId);
  const repliesByParentId = comments.reduce<Record<string, Comment[]>>((acc, c) => {
    if (c.parentCommentId) (acc[c.parentCommentId] ??= []).push(c);
    return acc;
  }, {});

  const handleDelete = () => {
    setMenuAnchor(null);
    onDelete(post.postId);
  };

  // Once the viewer blocks this author, the post disappears from the feed immediately.
  if (authorBlocked) return null;

  const reportedComment = reportTarget?.type === 'Comment'
    ? comments.find(c => c.commentId === reportTarget.commentId)
    : undefined;
  const reportOffender = reportTarget?.type === 'Comment'
    ? (reportedComment && reportedComment.authorUserId !== NEREUS_MENTION_ID
        ? { userId: reportedComment.authorUserId, displayName: reportedComment.authorDisplayName }
        : null)
    : (!isOwnPost && post.authorUserId !== NEREUS_MENTION_ID
        ? { userId: post.authorUserId, displayName: post.authorDisplayName }
        : null);

  return (
    <>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Avatar
          src={post.authorProfilePhotoUrl ?? undefined}
          sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
          onClick={() => navigate(`/u/${post.authorUserId}`)}
        >
          {initials(post.authorDisplayName) || '?'}
        </Avatar>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate(`/u/${post.authorUserId}`)}
          >
            {post.authorDisplayName}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {new Date(post.createdAt).toLocaleString()}
            </Typography>
            {post.visibility === 'Public' && (
              <Tooltip title="Public: anyone can see this, including people who aren't signed in">
                <PublicIcon aria-label="Public post" sx={{ fontSize: 14, color: 'text.secondary' }} />
              </Tooltip>
            )}
          </Stack>
        </Box>
        {post.isPinned && (
          <Chip icon={<PushPin fontSize="small" />} label="Pinned" size="small" color="primary" variant="outlined" />
        )}
        {post.isFeatured && (
          <Chip icon={<Star fontSize="small" />} label="Featured" size="small" color="warning" variant="outlined" />
        )}
        {(isOwnPost || canPin || currentUserId) && (
          <>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              {canPin && (
                <MenuItem onClick={handleTogglePin} disabled={pinning}>
                  <PushPin fontSize="small" sx={{ mr: 1 }} /> {post.isPinned ? 'Unpin' : 'Pin to top'}
                </MenuItem>
              )}
              {isOwnPost && !post.repostedFromPostId && (
                <MenuItem onClick={handleToggleFeature}>
                  <Star fontSize="small" sx={{ mr: 1 }} /> {post.isFeatured ? 'Remove from featured' : 'Feature on my profile'}
                </MenuItem>
              )}
              {isOwnPost && (
                <MenuItem onClick={handleDelete}>
                  <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
                </MenuItem>
              )}
              {!isOwnPost && currentUserId && (
                <MenuItem onClick={() => { setMenuAnchor(null); setReportTarget({ type: 'Post' }); }}>
                  <Flag fontSize="small" sx={{ mr: 1 }} /> Report post
                </MenuItem>
              )}
              {!isOwnPost && currentUserId && post.authorUserId !== NEREUS_MENTION_ID && (
                <MenuItem onClick={() => { setMenuAnchor(null); setBlockOpen(true); }} sx={{ color: 'error.main' }}>
                  <Block fontSize="small" sx={{ mr: 1 }} /> Block {post.authorDisplayName}
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Stack>

      {post.type === PostType.CheckIn && post.diveSiteName && (
        <Box sx={{ mb: post.text ? 1.5 : 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Place color="primary" fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Diving at{' '}
              {post.diveSiteSlug ? (
                <Link component={RouterLink} to={`/dive-sites/${post.diveSiteSlug}`} underline="hover">{post.diveSiteName}</Link>
              ) : post.diveSiteName}
            </Typography>
          </Stack>
        </Box>
      )}

      {post.type === PostType.ConditionsReport && post.conditions && (
        <Box sx={{ mb: post.text ? 1.5 : 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
            <Place color="primary" fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Conditions at{' '}
              {post.diveSiteSlug ? (
                <Link component={RouterLink} to={`/dive-sites/${post.diveSiteSlug}`} underline="hover">{post.diveSiteName}</Link>
              ) : post.diveSiteName}
            </Typography>
          </Stack>
          <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} alignItems="center">
            {post.conditions.visibilityMeters != null && <Chip size="small" label={`Visibility ${formatVisibility(post.conditions.visibilityMeters, units.depth)}`} />}
            {post.conditions.waterTemperatureC != null && <Chip size="small" label={`Water ${formatWaterTemp(post.conditions.waterTemperatureC, units.temperature)}`} />}
            {post.conditions.current && <Chip size="small" label={`${post.conditions.current} current`} />}
            {post.conditions.rating != null && <Rating value={post.conditions.rating} readOnly size="small" />}
          </Stack>
          <SafetyNote kind="conditions" sx={{ mt: 0.75 }} />
        </Box>
      )}

      {post.type === PostType.EquipmentAdded && post.equipmentSnapshot && (
        <Box sx={{ mb: post.text ? 1.5 : 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <EquipmentTypeIcon type={post.equipmentSnapshot.equipmentType as EquipmentType} size={20} framed={false} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Added new gear: {post.equipmentLabel}
            </Typography>
          </Stack>
        </Box>
      )}

      {post.type === PostType.DiveLog && post.diveSnapshot && (
        <Box sx={{ mb: post.text ? 1.5 : 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <ScubaDiving color="primary" fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {post.diveLabel}
            </Typography>
          </Stack>

          {post.diveSnapshot.diveProfile.length > 1 && (
            <DiveSparkline diveProfile={post.diveSnapshot.diveProfile} />
          )}

          {post.diveSnapshot.latitude != null && post.diveSnapshot.longitude != null && (
            <PostDiveMap latitude={post.diveSnapshot.latitude} longitude={post.diveSnapshot.longitude} />
          )}

          <Stack direction="row" spacing={2.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">
              Max depth <strong>{formatDepth(post.diveSnapshot.maxDepth, units.depth)}</strong>
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {formatDuration(post.diveSnapshot.duration)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Thermostat sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {formatTemperature(post.diveSnapshot.waterTemperature, units.temperature)}
              </Typography>
            </Stack>
          </Stack>

          {canCoLog && (
            <Button
              size="small"
              variant="outlined"
              startIcon={coLogging ? <CircularProgress size={14} /> : <ScubaDiving />}
              disabled={coLogging}
              onClick={handleCoLog}
              sx={{ mt: 1.5 }}
            >
              Co-log this dive
            </Button>
          )}
        </Box>
      )}

      {post.text && (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          <MentionText text={post.text} />
        </Typography>
      )}

      {(post.pendingTags?.length ?? 0) > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          With {post.pendingTags!.map(t => t.name).join(', ')}
          {isOwnPost && post.pendingTags!.some(t => !t.claimedByUserId) && (
            <>
              {' · '}
              <Link component="button" type="button" variant="body2" onClick={() => requestTagInvites(post.postId)} sx={{ verticalAlign: 'baseline' }}>
                Invite them
              </Link>
            </>
          )}
        </Typography>
      )}

      <PostMedia mediaKeys={post.photoKeys} />

      <Divider sx={{ mt: 1.5, mb: 0.5 }} />

      <Stack direction="row" spacing={0.5} alignItems="center">
        <IconButton
          size="small"
          onClick={handleReactionButtonClick}
          onContextMenu={(e) => { e.preventDefault(); openPicker(e); }}
          disabled={!currentUserId || reactionBusy}
        >
          {viewerReactions.length > 1 ? (
            <Stack direction="row" spacing={-0.5}>
              {viewerReactions.map(t => (
                <Typography key={t} component="span" sx={{ fontSize: 16 }}>{reactionEmoji(t)}</Typography>
              ))}
            </Stack>
          ) : (
            <Typography component="span" sx={{ fontSize: 16, filter: viewerReactions.length > 0 ? 'none' : 'grayscale(1) opacity(0.6)' }}>
              {reactionEmoji(viewerReactions[0] ?? ReactionType.Heart)}
            </Typography>
          )}
        </IconButton>
        <IconButton size="small" onClick={openPicker} disabled={!currentUserId || reactionBusy} sx={{ p: 0.25, mr: 0.5 }}>
          <ExpandMore sx={{ fontSize: 16 }} />
        </IconButton>
        {reactionCount > 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mr: 1.5, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={openReactorList}
          >
            {reactorLabel()}
          </Typography>
        )}
        <Popover
          open={!!reactorListAnchor}
          anchorEl={reactorListAnchor}
          onClose={closeReactorList}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Box sx={{ p: 1.5, minWidth: 220, maxWidth: 320, maxHeight: 320, overflowY: 'auto' }}>
            {reactorsLoading && reactors.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Stack spacing={1}>
                {reactors.map(reactor => {
                  const profile = reactorProfiles[reactor.userId];
                  const name = profile?.displayName ?? 'A diver';
                  return (
                    <Stack key={reactor.userId} direction="row" spacing={1} alignItems="center">
                      <Avatar
                        src={profile?.profilePhotoUrl ?? undefined}
                        sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: 11, fontWeight: 700 }}
                      >
                        {!profile?.profilePhotoUrl && (initials(name) || '?')}
                      </Avatar>
                      <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }}>{name}</Typography>
                      <Stack direction="row" spacing={-0.5}>
                        {reactor.types.map(t => (
                          <Typography key={t} component="span" sx={{ fontSize: 15 }}>{reactionEmoji(t)}</Typography>
                        ))}
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Box>
        </Popover>
        <Popover
          open={!!pickerAnchor}
          anchorEl={pickerAnchor}
          onClose={closePicker}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Stack direction="row" spacing={0.5} sx={{ p: 0.5 }}>
            {REACTIONS.map(r => (
              <IconButton
                key={r.type}
                size="small"
                title={r.label}
                onClick={() => chooseReaction(r.type)}
                sx={viewerReactions.includes(r.type) ? { bgcolor: 'action.selected' } : undefined}
              >
                <Typography component="span" sx={{ fontSize: 20 }}>{r.emoji}</Typography>
              </IconButton>
            ))}
          </Stack>
        </Popover>
        <IconButton size="small" onClick={() => setCommentsOpen(o => !o)}>
          <ChatBubbleOutline fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary" sx={{ mr: nested ? 0 : 1.5 }}>
          {commentCount > 0 ? commentCount : ''}
        </Typography>

        {!nested && currentUserId && post.visibility !== 'Private' && (
          <>
            <IconButton size="small" onClick={handleRepost} disabled={reposting} title="Repost">
              {reposting ? <CircularProgress size={16} /> : <ScubaDiving sx={{ fontSize: 16, transform: 'scaleX(-1)' }} />}
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {post.repostCount > 0 ? post.repostCount : ''}
            </Typography>
          </>
        )}
      </Stack>

      {commentsOpen && (
        <Box sx={{ mt: 1 }}>
          {commentsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {!commentsLoading && (
            <Stack spacing={1.25}>
              {topLevelComments.map(c => (
                <Box key={c.commentId}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Avatar
                      src={c.authorUserId === NEREUS_MENTION_ID ? undefined : (c.authorProfilePhotoUrl ?? undefined)}
                      sx={{ width: 28, height: 28, bgcolor: 'secondary.main', fontSize: 11, fontWeight: 700, cursor: c.authorUserId === NEREUS_MENTION_ID ? 'default' : 'pointer' }}
                      onClick={c.authorUserId === NEREUS_MENTION_ID ? undefined : () => navigate(`/u/${c.authorUserId}`)}
                    >
                      {c.authorUserId === NEREUS_MENTION_ID ? <NereusIcon sx={{ fontSize: 16 }} /> : (initials(c.authorDisplayName) || '?')}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                        {c.authorDisplayName}
                      </Typography>
                      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                        <MentionText text={c.text} />
                      </Typography>
                      {c.authorUserId === NEREUS_MENTION_ID && <NereusCaution variant="inline" />}
                      {currentUserId && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => startReply(c.commentId)}
                        >
                          Reply
                        </Typography>
                      )}
                    </Box>
                    {(currentUserId === c.authorUserId || isOwnPost) && (
                      <IconButton size="small" onClick={() => handleDeleteComment(c.commentId)}>
                        <Delete sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                    {currentUserId && currentUserId !== c.authorUserId && c.authorUserId !== NEREUS_MENTION_ID && (
                      <IconButton size="small" onClick={() => setReportTarget({ type: 'Comment', commentId: c.commentId })}>
                        <Flag sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Stack>

                  {(repliesByParentId[c.commentId]?.length ?? 0) > 0 && (
                    <Stack spacing={1} sx={{ mt: 1, pl: 4.5 }}>
                      {repliesByParentId[c.commentId].map(reply => (
                        <Stack key={reply.commentId} direction="row" spacing={1} alignItems="flex-start">
                          <Avatar
                            src={reply.authorUserId === NEREUS_MENTION_ID ? undefined : (reply.authorProfilePhotoUrl ?? undefined)}
                            sx={{ width: 24, height: 24, bgcolor: 'secondary.main', fontSize: 10, fontWeight: 700, cursor: reply.authorUserId === NEREUS_MENTION_ID ? 'default' : 'pointer' }}
                            onClick={reply.authorUserId === NEREUS_MENTION_ID ? undefined : () => navigate(`/u/${reply.authorUserId}`)}
                          >
                            {reply.authorUserId === NEREUS_MENTION_ID ? <NereusIcon sx={{ fontSize: 14 }} /> : (initials(reply.authorDisplayName) || '?')}
                          </Avatar>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                              {reply.authorDisplayName}
                            </Typography>
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                              <MentionText text={reply.text} />
                            </Typography>
                            {reply.authorUserId === NEREUS_MENTION_ID && <NereusCaution variant="inline" />}
                          </Box>
                          {(currentUserId === reply.authorUserId || isOwnPost) && (
                            <IconButton size="small" onClick={() => handleDeleteComment(reply.commentId)}>
                              <Delete sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                          {currentUserId && currentUserId !== reply.authorUserId && reply.authorUserId !== NEREUS_MENTION_ID && (
                            <IconButton size="small" onClick={() => setReportTarget({ type: 'Comment', commentId: reply.commentId })}>
                              <Flag sx={{ fontSize: 14 }} />
                            </IconButton>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}

                  {replyingToId === c.commentId && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1, pl: 4.5 }} alignItems="center">
                      <MentionTextField
                        size="small"
                        fullWidth
                        placeholder={`Reply to ${c.authorDisplayName}…`}
                        value={replyText}
                        onChange={setReplyText}
                        taggedUserIds={replyTaggedUserIds}
                        onTaggedUserIdsChange={setReplyTaggedUserIds}
                        taggedGroupIds={replyTaggedGroupIds}
                        onTaggedGroupIdsChange={setReplyTaggedGroupIds}
                        taggedClubIds={replyTaggedClubIds}
                        onTaggedClubIdsChange={setReplyTaggedClubIds}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddReply(); }}
                      />
                      <IconButton size="small" onClick={handleAddReply} disabled={!replyText.trim() || postingReply} color="primary">
                        {postingReply ? <CircularProgress size={16} /> : <Send fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" onClick={() => setReplyingToId(null)}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}
                </Box>
              ))}
            </Stack>
          )}

          {currentUserId && (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
              <MentionTextField
                size="small"
                fullWidth
                placeholder="Add a comment… Type @ to tag a buddy or group"
                value={newComment}
                onChange={setNewComment}
                taggedUserIds={commentTaggedUserIds}
                onTaggedUserIdsChange={setCommentTaggedUserIds}
                taggedGroupIds={commentTaggedGroupIds}
                onTaggedGroupIdsChange={setCommentTaggedGroupIds}
                taggedClubIds={commentTaggedClubIds}
                onTaggedClubIdsChange={setCommentTaggedClubIds}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment(); }}
              />
              <IconButton size="small" onClick={handleAddComment} disabled={!newComment.trim() || postingComment} color="primary">
                {postingComment ? <CircularProgress size={16} /> : <Send fontSize="small" />}
              </IconButton>
            </Stack>
          )}
        </Box>
      )}

      {reportTarget && (
        <ReportDialog
          target={reportTarget}
          postId={post.postId}
          offender={reportOffender}
          onClose={() => setReportTarget(null)}
          onBlocked={userId => {
            if (userId === post.authorUserId) setAuthorBlocked(true);
            setComments(prev => prev.filter(c => c.authorUserId !== userId));
          }}
        />
      )}

      <BlockUserDialog
        open={blockOpen}
        userId={post.authorUserId}
        displayName={post.authorDisplayName}
        onClose={() => setBlockOpen(false)}
        onBlocked={() => { setBlockOpen(false); setAuthorBlocked(true); }}
      />
    </>
  );
};

export default PostCard;
